/**
 * GoogieInvoiceEditor — an invoice, shaped like an invoice.
 *
 * INVOICE-2. Asked where an invoice should live, Rennan picked "its
 * own editor, same plumbing": a purpose-built screen that still saves
 * as a `documents` row of kind 'invoice', so Stripe Connect, deposits,
 * instalments and the public /d/:token page all come for free.
 *
 * The reason not to reuse Contract Studio is shape, not capability.
 * It is a block editor — drag a heading, drag a paragraph — which is
 * exactly right for a proposal and wrong for an invoice. An invoice
 * is a numbered table that totals itself, and the things a studio
 * changes are rows, a rate and a date, not the order of the blocks.
 * Those rows are still stored AS blocks, so a document that has been
 * through Contract Studio opens here and back again without loss.
 *
 * TYPED, THEN SAVED. Every field is local state until Save. A
 * line-item table that writes on each keystroke is a table that
 * fights the person typing in it, and the per-block mutations would
 * make twenty round trips of a ten-row invoice with nothing holding
 * them together.
 *
 * It works before 20261029160000_invoice_fields.sql lands. The
 * number, dates, tax and terms go in a second statement, and if that
 * one fails because the columns are not there yet, the lines, the
 * total and the title are already saved and the studio is told
 * exactly what did not stick.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { Link as LinkIcon, Plus, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { G } from '@/lib/googie';
import { useClients, useCreateClient } from '@/services/clients';
import { useIntakeForms } from '@/services/intakeForms';
import { useOrganization } from '@/services/organizations';
import { useProjects, useCreateProject } from '@/services/projects';
import { useDocument, useDocumentBlocks, useCreateDocument, useSendDocument } from '@/services/documents';
import { useShareOrigin } from '@/hooks/useShareOrigin';
import { invoiceMath, money, useSaveInvoice, type InvoiceDraft, type InvoiceLine } from '@/services/invoicing';
import { GoogieScreen } from '../GoogieShell';
import { GoogieCapsule, GoogieHeader, GoogieMasthead, GoogiePage } from '../primitives';
// NOVA-LOAD-1 — `OrbitLoader`, not `NovaSpinner`. The spinner is a
// deliberate 16px CROP of Nova's antenna for buttons and table cells,
// and at card size it reads as the wrong character (Rennan: "its the
// wrong 2d nova"). OrbitLoader is the canonical 2026 kit avatar,
// floating on the blue glow, with the rainbow eye-build.
import OrbitLoader from '@/components/shared/OrbitLoader';
import { cn } from '@/lib/utils';

const today = () => format(new Date(), 'yyyy-MM-dd');
const inDays = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return format(d, 'yyyy-MM-dd');
};

const newLine = (): InvoiceLine => ({
  key: Math.random().toString(36).slice(2),
  name: '',
  description: '',
  quantity: 1,
  unitCents: 0,
});

/** "1,250.50" → 125050. Anything unparseable is zero, not NaN. */
const toCents = (text: string): number => {
  const n = Number(String(text).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
};
const fromCents = (cents: number): string => (cents / 100).toFixed(2);

const EMPTY: InvoiceDraft = {
  title: '',
  clientId: null,
  projectId: null,
  invoiceNumber: '',
  issuedOn: today(),
  // Net 30, which is what most of these are, and a date the studio
  // can change rather than a box they have to fill.
  dueOn: inDays(30),
  taxRate: 0,
  notes: '',
  lines: [newLine()],
  depositPercent: 0,
  formId: null,
};

export function GoogieInvoiceEditor() {
  const { documentId } = useParams<{ documentId: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const isNew = !documentId || documentId === 'new';

  const { data: doc, isLoading: loadingDoc } = useDocument(isNew ? undefined : documentId);
  const { data: blocks = [], isLoading: loadingBlocks } = useDocumentBlocks(isNew ? undefined : documentId);
  const { data: clients = [] } = useClients();
  const { data: projects = [] } = useProjects();
  const { data: forms = [] } = useIntakeForms();
  const { data: organization } = useOrganization();
  const createClient = useCreateClient();
  const createProject = useCreateProject();
  const createDocument = useCreateDocument();
  const saveInvoice = useSaveInvoice();
  const sendDocument = useSendDocument();
  const shareOrigin = useShareOrigin();

  const [draft, setDraft] = useState<InvoiceDraft>(() => ({
    ...EMPTY,
    projectId: params.get('project'),
    clientId: params.get('client'),
  }));
  const [loaded, setLoaded] = useState(false);

  // Fill the draft once, from whatever is on the document. After that
  // the draft is the truth until Save — refetches must not overwrite
  // what somebody is in the middle of typing.
  useEffect(() => {
    if (isNew || loaded || loadingDoc || loadingBlocks || !doc) return;
    const d = doc as unknown as Record<string, unknown>;
    const lines: InvoiceLine[] = [];
    let depositPercent = 0;
    let notes = '';
    let formId: string | null = null;
    for (const b of blocks as Array<{ type: string; payload: Record<string, unknown> }>) {
      const p = b.payload ?? {};
      if (b.type === 'lineitem') {
        lines.push({
          key: Math.random().toString(36).slice(2),
          name: String(p.name ?? ''),
          description: String(p.description ?? ''),
          quantity: Number(p.quantity ?? 1) || 1,
          unitCents: Number(p.price_cents ?? 0) || 0,
        });
      } else if (b.type === 'deposit') {
        depositPercent = Number(p.percentage ?? 0) || 0;
      } else if (b.type === 'terms') {
        notes = String(p.text ?? '');
      } else if (b.type === 'form_link') {
        formId = (p.form_id as string | undefined) ?? null;
      }
    }
    setDraft({
      title: String(d.title ?? ''),
      clientId: (d.client_id as string | null) ?? null,
      projectId: (d.project_id as string | null) ?? null,
      invoiceNumber: String(d.invoice_number ?? ''),
      issuedOn: (d.issued_on as string | null) ?? today(),
      dueOn: (d.due_on as string | null) ?? inDays(30),
      taxRate: Number(d.tax_rate ?? 0) || 0,
      notes: notes || String(d.invoice_notes ?? ''),
      lines: lines.length ? lines : [newLine()],
      depositPercent,
      formId,
    });
    setLoaded(true);
  }, [blocks, doc, isNew, loaded, loadingBlocks, loadingDoc]);

  const totals = useMemo(() => invoiceMath(draft), [draft]);

  /**
   * INVOICE-8 — making a client or a project without leaving.
   *
   * Rennan: *"when creating an invoice, the ability to add or create
   * a new customer, and the same for a project."* Sending someone to
   * /clients to type a name and come back loses the invoice they were
   * halfway through — and an invoice is very often the FIRST thing a
   * studio makes for a new client, so "pick one that already exists"
   * is the wrong default for the commonest case.
   *
   * Deliberately the two fields that matter and nothing else. The
   * full forms live on their own pages; this is the shortcut, and a
   * shortcut that asks for nine fields is not one.
   */
  const [adding, setAdding] = useState<null | 'client' | 'project'>(null);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');

  const addInline = async () => {
    const name = newName.trim();
    if (!name) return;
    if (!organization?.id) {
      toast.error('Business not ready — try again in a moment');
      return;
    }
    try {
      if (adding === 'client') {
        const made = await createClient.mutateAsync({
          name,
          email: newEmail.trim() || null,
          org_id: organization.id,
        } as never);
        patch({ clientId: (made as unknown as { id: string }).id });
        toast.success(`${name} added`);
      } else {
        const made = await createProject.mutateAsync({
          name,
          // Carried over if one is already picked, so a project made
          // from an invoice belongs to the client being invoiced.
          client_id: draft.clientId,
          org_id: organization.id,
          status: 'discovery',
        } as never);
        patch({ projectId: (made as unknown as { id: string }).id });
        toast.success(`${name} created`);
      }
      setAdding(null);
      setNewName('');
      setNewEmail('');
    } catch (err) {
      toast.error((err as Error)?.message || "That didn't save");
    }
  };

  const sentAt = (doc as unknown as { sent_at?: string | null } | undefined)?.sent_at ?? null;

  /**
   * INVOICE-7 — is the link actually SERVED yet?
   *
   * `get_public_document_by_token` answers only for a document whose
   * status is one of sent / viewed / signed / paid. A draft's token
   * exists from the moment the row is inserted, and the page it
   * points at says "Document not found. This link may have expired or
   * been revoked."
   *
   * So the link was offered before it worked: copy it off a draft,
   * send it to a client, and they get a dead page that blames the
   * link. This is the condition the database actually applies, rather
   * than "has a token".
   */
  const LIVE_STATUSES = ['sent', 'viewed', 'signed', 'paid'];
  const status = (doc as unknown as { status?: string } | undefined)?.status ?? 'draft';
  const isLive = LIVE_STATUSES.includes(status);

  /** The page the client opens — only once it would answer. */
  const publicUrl = useMemo(() => {
    const token = (doc as unknown as { public_token?: string } | undefined)?.public_token;
    return token && isLive ? `${shareOrigin}/d/${token}` : null;
  }, [doc, isLive, shareOrigin]);
  const patch = (p: Partial<InvoiceDraft>) => setDraft((d) => ({ ...d, ...p }));
  const patchLine = (key: string, p: Partial<InvoiceLine>) =>
    setDraft((d) => ({ ...d, lines: d.lines.map((l) => (l.key === key ? { ...l, ...p } : l)) }));

  /**
   * The draft, with the chosen form's name and public URL filled in.
   *
   * Resolved here rather than at render time because the page the
   * client opens is anonymous and cannot read `intake_forms` by id
   * past the SEC-1 lockdown. Re-resolved on every save, so renaming a
   * form and saving the invoice again refreshes the label.
   */
  const withForm = (d: InvoiceDraft): InvoiceDraft => {
    if (!d.formId) return { ...d, formLabel: undefined, formUrl: undefined };
    const form = (forms as Array<{ id?: string; name?: string; slug?: string }>).find(
      (f) => f.id === d.formId,
    );
    const orgSlug = (organization as { slug?: string } | undefined)?.slug;
    return {
      ...d,
      formLabel: form?.name,
      formUrl:
        form?.slug && orgSlug ? `${shareOrigin}/embed/intake/${orgSlug}/${form.slug}` : undefined,
    };
  };

  const save = async () => {
    if (!draft.title.trim()) {
      toast.error('Give the invoice a title first');
      return;
    }
    try {
      let id = isNew ? null : documentId!;
      if (!id) {
        const created = await createDocument.mutateAsync({
          kind: 'invoice',
          title: draft.title.trim(),
          clientId: draft.clientId,
          projectId: draft.projectId,
        });
        id = (created as unknown as { id: string }).id;
      }
      const { fieldsMissing } = await saveInvoice.mutateAsync({ id, draft: withForm(draft) });
      if (fieldsMissing) {
        // Honest rather than silent: the money is saved, the
        // paperwork around it is not, and the studio can decide
        // whether to care.
        toast.warning('Saved — but the number, dates and tax need a database migration that hasn’t run yet.');
      } else {
        toast.success('Invoice saved');
      }
      if (isNew) navigate(`/invoices/${id}`, { replace: true });
    } catch (err) {
      toast.error((err as Error)?.message || "That didn't save");
    }
  };

  const copyLink = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl).then(
      () => toast.success('Link copied'),
      () => toast.error("Couldn't copy"),
    );
  };

  /**
   * Send marks it sent, which is what opens the public page: the RLS
   * policy behind /d/:token gates on the document's status, so an
   * invoice left in draft is a link that shows a client nothing.
   *
   * It saves first. Sending the version on the server rather than the
   * version on screen is how a client gets an invoice the studio has
   * already changed.
   */
  const send = async () => {
    if (isNew || !documentId) {
      toast.error('Save it first — the link is minted when the invoice is created');
      return;
    }
    try {
      await saveInvoice.mutateAsync({ id: documentId, draft: withForm(draft) });
      await sendDocument.mutateAsync({ id: documentId, clientId: draft.clientId });
      toast.success(publicUrl ? 'Marked as sent — the link is live' : 'Marked as sent');
    } catch (err) {
      toast.error((err as Error)?.message || "That didn't send");
    }
  };

  if (!isNew && (loadingDoc || loadingBlocks)) {
    return (
      <GoogieScreen>
        <GoogiePage>
          <div className="g-card flex items-center justify-center p-16">
            <OrbitLoader size="lg" label="Opening the invoice" />
          </div>
        </GoogiePage>
      </GoogieScreen>
    );
  }

  const busy = saveInvoice.isPending || createDocument.isPending || sendDocument.isPending;

  return (
    <GoogieScreen>
      <GoogieHeader capsule={<GoogieCapsule dot={G.violet}>Money</GoogieCapsule>} />

      <GoogiePage>
        <GoogieMasthead
          eyebrow={isNew ? 'NEW · INVOICE' : `INVOICE${draft.invoiceNumber ? ` · ${draft.invoiceNumber}` : ''}`}
          eyebrowColor={G.violet}
          title={draft.title || 'Untitled invoice'}
          actions={
            <>
              <button type="button" className="g-pill" onClick={() => navigate('/invoices')}>
                All invoices
              </button>
              {publicUrl && (
                <button type="button" className="g-pill" onClick={copyLink}>
                  <LinkIcon className="h-5 w-5" aria-hidden /> Copy link
                </button>
              )}
              {!isNew && (
                // Send is the SOLID pill until it has been sent: it is
                // the thing that makes the link work, and the link is
                // the whole point of an invoice.
                <button
                  type="button"
                  className={cn('g-pill', !isLive && 'g-pill-solid')}
                  onClick={() => void send()}
                  disabled={busy}
                >
                  <Send className="h-5 w-5" aria-hidden /> {sentAt ? 'Send again' : 'Send'}
                </button>
              )}
              <button type="button" className="g-pill g-pill-solid" onClick={() => void save()} disabled={busy}>
                {busy ? 'Saving…' : 'Save'}
              </button>
            </>
          }
        />

        <div className="mt-7 grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
          {/* ── the invoice ──────────────────────────────── */}
          <div className="flex flex-col gap-5">
            <section className="g-card p-5">
              <Field label="Title">
                <input
                  className="g-input"
                  value={draft.title}
                  onChange={(e) => patch({ title: e.target.value })}
                  placeholder="Wedding photography — the Martinezes"
                />
              </Field>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Field label="Number">
                  <input
                    className="g-input"
                    value={draft.invoiceNumber}
                    onChange={(e) => patch({ invoiceNumber: e.target.value })}
                    placeholder="2026-014"
                  />
                </Field>
                <Field label="Issued">
                  <input
                    type="date"
                    className="g-input"
                    value={draft.issuedOn ?? ''}
                    onChange={(e) => patch({ issuedOn: e.target.value || null })}
                  />
                </Field>
                <Field label="Due">
                  <input
                    type="date"
                    className="g-input"
                    value={draft.dueOn ?? ''}
                    onChange={(e) => patch({ dueOn: e.target.value || null })}
                  />
                </Field>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Client">
                  <div className="flex gap-2">
                    <select
                      className="g-input"
                      value={draft.clientId ?? ''}
                      onChange={(e) => patch({ clientId: e.target.value || null })}
                    >
                      <option value="">No client</option>
                      {(clients as Array<{ id?: string; name?: string }>).map((c) =>
                        c.id ? (
                          <option key={c.id} value={c.id}>
                            {c.name ?? 'Client'}
                          </option>
                        ) : null,
                      )}
                    </select>
                    <button
                      type="button"
                      className="g-pill !h-[42px] shrink-0"
                      onClick={() => {
                        setAdding('client');
                        setNewName('');
                        setNewEmail('');
                      }}
                    >
                      <Plus className="h-4 w-4" aria-hidden /> New
                    </button>
                  </div>
                </Field>
                <Field label="Project" hint="Optional — an invoice doesn’t need one.">
                  <div className="flex gap-2">
                    <select
                      className="g-input"
                      value={draft.projectId ?? ''}
                      onChange={(e) => patch({ projectId: e.target.value || null })}
                    >
                      <option value="">No project</option>
                      {(projects as Array<{ id?: string; name?: string }>).map((p) =>
                        p.id ? (
                          <option key={p.id} value={p.id}>
                            {p.name ?? 'Project'}
                          </option>
                        ) : null,
                      )}
                    </select>
                    <button
                      type="button"
                      className="g-pill !h-[42px] shrink-0"
                      onClick={() => {
                        setAdding('project');
                        setNewName('');
                        setNewEmail('');
                      }}
                    >
                      <Plus className="h-4 w-4" aria-hidden /> New
                    </button>
                  </div>
                </Field>
              </div>

              {adding && (
                <div className="mt-3 rounded-xl border border-border p-3">
                  <p className="g-th pb-2">
                    {adding === 'client' ? 'New client' : 'New project'}
                  </p>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <input
                      className="g-input"
                      autoFocus
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void addInline();
                        if (e.key === 'Escape') setAdding(null);
                      }}
                      placeholder={adding === 'client' ? 'Sarah & Ben Martinez' : 'Martinez — Wedding'}
                      aria-label={adding === 'client' ? 'Client name' : 'Project name'}
                    />
                    {adding === 'client' && (
                      <input
                        className="g-input"
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void addInline();
                          if (e.key === 'Escape') setAdding(null);
                        }}
                        placeholder="Email (optional)"
                        aria-label="Client email"
                      />
                    )}
                    <button
                      type="button"
                      className="g-pill g-pill-solid shrink-0"
                      onClick={() => void addInline()}
                      disabled={!newName.trim() || createClient.isPending || createProject.isPending}
                    >
                      Add
                    </button>
                    <button type="button" className="g-pill shrink-0" onClick={() => setAdding(null)}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {forms.length > 0 && (
                <div className="mt-4">
                  <Field
                    label="Form"
                    hint="Optional — shown on the invoice as something for them to fill in."
                  >
                    <select
                      className="g-input"
                      value={draft.formId ?? ''}
                      onChange={(e) => patch({ formId: e.target.value || null })}
                    >
                      <option value="">No form</option>
                      {(forms as Array<{ id?: string; name?: string; is_active?: boolean }>).map((f) =>
                        f.id ? (
                          <option key={f.id} value={f.id}>
                            {f.name ?? 'Form'}
                            {f.is_active === false ? ' (inactive)' : ''}
                          </option>
                        ) : null,
                      )}
                    </select>
                  </Field>
                </div>
              )}
            </section>

            {/* The table. Headings on sm+ only — at phone width each
                row stacks and labelled fields read better than a
                four-column grid nobody can hit. */}
            <section className="g-card p-5">
              <div className="mb-3 hidden grid-cols-[minmax(0,1fr)_72px_120px_110px_32px] gap-3 sm:grid">
                <span className="g-th">Item</span>
                <span className="g-th text-right">Qty</span>
                <span className="g-th text-right">Unit</span>
                <span className="g-th text-right">Amount</span>
                <span />
              </div>

              <div className="flex flex-col gap-3">
                {draft.lines.map((l) => (
                  <div
                    key={l.key}
                    className="grid grid-cols-1 gap-2 border-b border-border/50 pb-3 last:border-0 last:pb-0 sm:grid-cols-[minmax(0,1fr)_72px_120px_110px_32px] sm:items-start sm:gap-3 sm:border-0 sm:pb-0"
                  >
                    <div className="flex flex-col gap-1.5">
                      <input
                        className="g-input"
                        value={l.name}
                        onChange={(e) => patchLine(l.key, { name: e.target.value })}
                        placeholder="Full-day coverage"
                        aria-label="Item"
                      />
                      <input
                        className="g-input !text-[14px]"
                        value={l.description ?? ''}
                        onChange={(e) => patchLine(l.key, { description: e.target.value })}
                        placeholder="Description (optional)"
                        aria-label="Description"
                      />
                    </div>
                    <input
                      className="g-input text-right"
                      inputMode="decimal"
                      value={String(l.quantity)}
                      onChange={(e) => patchLine(l.key, { quantity: Number(e.target.value) || 0 })}
                      aria-label="Quantity"
                    />
                    <input
                      className="g-input text-right"
                      inputMode="decimal"
                      value={fromCents(l.unitCents)}
                      onChange={(e) => patchLine(l.key, { unitCents: toCents(e.target.value) })}
                      aria-label="Unit price"
                    />
                    <span className="g-display self-center text-right text-[17px] tabular-nums">
                      {money(Math.round(l.quantity * l.unitCents))}
                    </span>
                    <button
                      type="button"
                      className="g-pill !h-9 !w-9 !p-0 justify-self-end"
                      aria-label={`Remove ${l.name || 'this line'}`}
                      onClick={() =>
                        setDraft((d) => ({
                          ...d,
                          // Never zero rows: an empty table has
                          // nowhere to start typing.
                          lines: d.lines.length > 1 ? d.lines.filter((x) => x.key !== l.key) : [newLine()],
                        }))
                      }
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </button>
                  </div>
                ))}
              </div>

              <button
                type="button"
                className="g-pill mt-4"
                onClick={() => setDraft((d) => ({ ...d, lines: [...d.lines, newLine()] }))}
              >
                <Plus className="h-5 w-5" aria-hidden /> Add a line
              </button>
            </section>

            <section className="g-card p-5">
              <Field label="Terms" hint="Payment terms, bank details, the thank-you under the total.">
                <textarea
                  className="g-input min-h-[96px] py-2"
                  value={draft.notes}
                  onChange={(e) => patch({ notes: e.target.value })}
                  placeholder="Payment due within 30 days. Thank you — it was a joy to shoot."
                />
              </Field>
            </section>
          </div>

          {/* ── the totals ───────────────────────────────── */}
          <aside className="g-card flex flex-col gap-4 p-5 lg:sticky lg:top-4">
            <h2 className="g-h3">Totals</h2>
            <Row label="Subtotal" value={money(totals.subtotalCents)} />
            <div className="flex items-center justify-between gap-3">
              <label className="g-mute flex items-center gap-2 text-[14.5px]">
                Tax
                <input
                  className="g-input !h-8 !w-16 text-right"
                  inputMode="decimal"
                  value={String(draft.taxRate)}
                  onChange={(e) => patch({ taxRate: Number(e.target.value) || 0 })}
                  aria-label="Tax rate, percent"
                />
                %
              </label>
              <span className="g-display text-[17px] tabular-nums">{money(totals.taxCents)}</span>
            </div>
            <div className="border-t border-border/60 pt-4">
              <Row label="Total" value={money(totals.totalCents)} big />
            </div>

            <div className="border-t border-border/60 pt-4">
              <label className="g-mute flex items-center gap-2 text-[14.5px]">
                Deposit
                <input
                  className="g-input !h-8 !w-16 text-right"
                  inputMode="decimal"
                  value={String(draft.depositPercent)}
                  onChange={(e) => patch({ depositPercent: Number(e.target.value) || 0 })}
                  aria-label="Deposit percent"
                />
                %
              </label>
              <p className="g-mute mt-2 text-[13px]">
                {draft.depositPercent > 0
                  ? `${money(totals.depositCents)} due up front, ${money(totals.totalCents - totals.depositCents)} after.`
                  : 'Nothing up front. Set a percentage to ask for a deposit.'}
              </p>
            </div>

            <button
              type="button"
              className="g-pill g-pill-solid mt-2 justify-center"
              onClick={() => void save()}
              disabled={busy}
            >
              {busy ? 'Saving…' : 'Save invoice'}
            </button>
            <p className="g-mute text-[13px]">
              {isNew
                ? 'Save it, then Send — the link only works once it has been sent.'
                : isLive
                  ? `Sent${sentAt ? ` ${format(new Date(sentAt), 'd MMM')}` : ''}. The link is live and your client can pay by card on it.`
                  : 'Not sent yet. The link exists but shows your client “Document not found” until you Send — that is the database refusing to serve a draft, not a broken link.'}
            </p>
          </aside>
        </div>
      </GoogiePage>
    </GoogieScreen>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="g-th block pb-1.5">{label}</span>
      {children}
      {hint && <span className="g-mute mt-1 block text-[13px]">{hint}</span>}
    </label>
  );
}

function Row({ label, value, big }: { label: string; value: string; big?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className={cn('g-mute text-[14.5px]', big && 'text-foreground')}>{label}</span>
      <span className={cn('g-display tabular-nums', big ? 'text-[26px]' : 'text-[17px]')}>{value}</span>
    </div>
  );
}

export default GoogieInvoiceEditor;
